import { useTranslation } from 'react-i18next';

import { FACTOR_STATES, type FactorStateKind } from '../framework';
import { StateIcon } from './StateIcon';
import './StatePicker.css';

type Props = {
  /** Current selected state, or null if no selection yet. */
  readonly value: FactorStateKind | null;
  /** Called when the user picks a state. */
  readonly onChange: (kind: FactorStateKind) => void;
  /** Accessible label tying the picker to the subdomain it controls. */
  readonly ariaLabel: string;
  /** Disabled while a save is in flight or for read-only consumers. */
  readonly disabled?: boolean;
};

/**
 * Segmented control for the four factor states.
 *
 * Pairs each state's colour with a distinct glyph so the picker is
 * comprehensible without relying on colour alone (WCAG SC 1.4.1).
 *
 * The order — risk → change → protective → not-assessed — runs the framework's
 * spectrum from left to right plus the meta state at the end. Matches the
 * left-to-right flow of how kaimahi describe shifts during practice
 * conversations.
 */
export function StatePicker({
  value,
  onChange,
  ariaLabel,
  disabled = false,
}: Props) {
  const { t } = useTranslation();

  // Order: risk → change → protective → not-assessed.
  // The numeric coding (1/2/3/0) preserved for backend compatibility lives in
  // FACTOR_STATE_NUMERIC; the picker's visual order is a UX choice.
  const orderedKinds: readonly FactorStateKind[] = [
    'risk',
    'change',
    'protective',
    'not-assessed',
  ];

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className="state-picker"
    >
      {orderedKinds.map((kind) => {
        const state = FACTOR_STATES[kind];
        const isSelected = value === kind;
        return (
          <button
            key={kind}
            type="button"
            data-state={kind}
            aria-pressed={isSelected}
            disabled={disabled}
            className="state-picker__option"
            onClick={() => onChange(kind)}
          >
            <StateIcon state={kind} className="state-picker__icon" />
            <span className="state-picker__labels">
              {state.teReoName ? (
                <span className="state-picker__label-mi" lang="mi">
                  {state.teReoName}
                </span>
              ) : null}
              <span className="state-picker__label-en">
                {t(`framework.state.${
                  kind === 'not-assessed' ? 'notAssessed' : kind
                }.descriptor`)}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default StatePicker;
